import React, { useState, useEffect, useRef } from 'react';

/**
 * 커스텀 셀렉트 컴포넌트 (공통 컴포넌트)
 * @param {string} value - 현재 선택된 값
 * @param {Array} options - 옵션 목록 [{ value: 'val', label: 'Text' }, ...]
 * @param {Function} onChange - 값 변경 핸들러 (e.target.value 형태로 전달됨)
 * @param {string} className - 추가적인 클래스명 (선택 사항)
 * @param {object} style - 추가적인 스타일 (선택 사항)
 * @param {'bottom'|'top'} menuPlacement - 드롭다운 펼침 방향 (기본: bottom)
 * @param {boolean} showChevron - 화살표 아이콘 표시 여부 (기본: true)
 * @param {'default'|'icon'|'primary'} triggerVariant - 트리거 버튼 스타일 (기본: default)
 * @param {string} triggerIcon - triggerVariant가 icon일 때 material icon 이름
 * @param {string} triggerTitle - 트리거 버튼 title 속성
 * @param {string} wrapperClassName - 래퍼 div 클래스명
 * @param {string} menuClassName - 드롭다운 메뉴 추가 클래스명
 * @param {'left'|'right'} menuAlign - 드롭다운 메뉴 정렬 (기본: left)
 * @param {boolean} stopPropagation - 클릭 이벤트 버블링 차단 (기본: false)
 * @param {boolean} disabled - 선택 비활성화 여부 (기본: false)
 */
function SelectorCustom({
    value,
    options,
    onChange,
    className = '',
    style = {},
    menuPlacement = 'bottom',
    showChevron = true,
    triggerVariant = 'default',
    triggerIcon = '',
    triggerTitle = '',
    wrapperClassName = '',
    menuClassName = '',
    menuAlign = 'left',
    stopPropagation = false,
    disabled = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    // 외부 클릭 감지하여 드롭다운 닫기
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const handleOptionClick = (optionValue) => {
        // 기존 이벤트 객체 형태를 흉내내어 onChange 호출
        onChange({ target: { value: optionValue } });
        setIsOpen(false);
    };

    const selectedLabel = options.find(opt => opt.value === value)?.label || '선택하세요';

    const menuClassNames = [
        'select-dropdown-menu',
        menuPlacement === 'top' ? 'select-dropdown-menu--top' : '',
        menuAlign === 'right' ? 'select-dropdown-menu--align-right' : '',
        menuClassName,
    ].filter(Boolean).join(' ');

    const handleWrapperEvent = stopPropagation
        ? (e) => e.stopPropagation()
        : undefined;

    const toggleOpen = () => {
        if (!disabled) setIsOpen((prev) => !prev);
    };

    const triggerButtonClass = (() => {
        if (triggerVariant === 'icon') {
            return `btn-sqr btn-trans${isOpen ? ' active' : ''} ${className}`.trim();
        }
        const variantClass = triggerVariant === 'primary' ? 'btn-primary' : 'btn-line';
        return `btn-rtg ${variantClass}${isOpen ? ' active' : ''} ${className}`.trim();
    })();

    return (
        <div
            ref={wrapperRef}
            className={wrapperClassName}
            style={{ position: 'relative', minWidth: '100px', ...style }}
            onClick={handleWrapperEvent}
            onMouseDown={handleWrapperEvent}
        >
            {triggerVariant === 'icon' ? (
                <button
                    className={triggerButtonClass}
                    onClick={toggleOpen}
                    type="button"
                    title={triggerTitle}
                    disabled={disabled}
                >
                    <span className="material-symbols-rounded">{triggerIcon}</span>
                </button>
            ) : (
                <button
                    className={triggerButtonClass}
                    onClick={toggleOpen}
                    style={{ width: '100%', justifyContent: showChevron ? 'space-between' : 'center' }}
                    type="button"
                    title={triggerTitle || undefined}
                    disabled={disabled}
                >
                    <span>{selectedLabel}</span>
                    {showChevron && (
                        <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>
                            {isOpen ? 'expand_less' : 'expand_more'}
                        </span>
                    )}
                </button>
            )}

            {isOpen && (
                <div className={menuClassNames}>
                    {options.map((option) => (
                        <div 
                            key={option.value} 
                            className={`select-dropdown-item ${value === option.value ? 'selected' : ''}`}
                            onClick={() => handleOptionClick(option.value)}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SelectorCustom;

